import { makeAutoObservable, runInAction } from 'mobx';

/**
 * Export quality presets with bitrate configurations
 */
export const VIDEO_QUALITY_PRESETS = {
  high: { label: 'High', videoBitrate: 12e6, audioBitrate: 192e3, description: '12 Mbps' },
  recommended: { label: 'Recommended', videoBitrate: 8e6, audioBitrate: 128e3, description: '8 Mbps' },
  low: { label: 'Low', videoBitrate: 4e6, audioBitrate: 96e3, description: '4 Mbps' },
} as const;

export const AUDIO_QUALITY_PRESETS = {
  high: { label: 'High', bitrate: 320e3, description: '320 kbps' },
  recommended: { label: 'Recommended', bitrate: 192e3, description: '192 kbps' },
  low: { label: 'Low', bitrate: 128e3, description: '128 kbps' },
} as const;

export const RESOLUTION_PRESETS = [
  { label: '480p', shortSide: 480 },
  { label: '720p', shortSide: 720 },
  { label: '1080p', shortSide: 1080 },
  { label: '1440p', shortSide: 1440 },
  { label: '4k', shortSide: 2160 },
] as const;

export type ExportType = 'video' | 'audio' | 'transcript';
export type QualityLevel = 'high' | 'recommended' | 'low';
export type ExportStatus = 'idle' | 'preparing' | 'exporting' | 'completed' | 'error' | 'cancelled';

interface ExportState {
  status: ExportStatus;
  type: ExportType;
  progress: number; // 0-100
  currentFrame: number;
  totalFrames: number;
  remainingMs: number;
  filename: string;
  error: string | null;
  startedAt: number | null;
  completedAt: number | null;
}

class ExportStoreClass {
  // Current export state
  state: ExportState = {
    status: 'idle',
    type: 'video',
    progress: 0,
    currentFrame: 0,
    totalFrames: 0,
    remainingMs: 0,
    filename: '',
    error: null,
    startedAt: null,
    completedAt: null,
  };

  // Export settings (remembered between exports)
  selectedQuality: QualityLevel = 'recommended';
  selectedResolution: string = '1080p';

  // Abort controller for cancellation
  private abortController: AbortController | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Computed properties
  // ─────────────────────────────────────────────────────────────────────────────

  get isExporting(): boolean {
    return this.state.status === 'preparing' || this.state.status === 'exporting';
  }

  get isCompleted(): boolean {
    return this.state.status === 'completed';
  }

  get hasError(): boolean {
    return this.state.status === 'error';
  }

  get progressPercent(): number {
    return Math.round(this.state.progress);
  }

  get elapsedMs(): number {
    if (!this.state.startedAt) return 0;
    const end = this.state.completedAt || Date.now();
    return end - this.state.startedAt;
  }

  get remainingTimeFormatted(): string {
    const ms = this.state.remainingMs;
    if (ms <= 0) return '';
    if (ms < 60000) return 'less than a minute remaining';
    const mins = Math.ceil(ms / 60000);
    return `about ${mins} minute${mins > 1 ? 's' : ''} remaining`;
  }

  get statusLabel(): string {
    switch (this.state.status) {
      case 'preparing': return 'Preparing export...';
      case 'exporting':
        if (this.state.type === 'video') return 'Exporting video';
        if (this.state.type === 'audio') return 'Exporting audio';
        return 'Exporting transcript';
      case 'completed': return 'Export complete';
      case 'error': return 'Export failed';
      case 'cancelled': return 'Export cancelled';
      default: return '';
    }
  }

  /**
   * Estimate file size based on duration and quality settings
   */
  estimateFileSize(durationSec: number, type: ExportType, quality: QualityLevel): string {
    if (durationSec <= 0) return '—';
    
    let bitrate: number;
    if (type === 'video') {
      const preset = VIDEO_QUALITY_PRESETS[quality];
      bitrate = preset.videoBitrate + preset.audioBitrate;
    } else if (type === 'audio') {
      bitrate = AUDIO_QUALITY_PRESETS[quality].bitrate;
    } else {
      return '~1 KB';
    }
    
    const sizeBytes = (bitrate / 8) * durationSec;
    const sizeMB = sizeBytes / (1024 * 1024);
    
    if (sizeMB < 1) return `~${Math.ceil(sizeMB * 1024)} KB`;
    if (sizeMB < 1024) return `~${sizeMB.toFixed(0)} MB`;
    return `~${(sizeMB / 1024).toFixed(1)} GB`;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────────

  setQuality(quality: QualityLevel): void {
    this.selectedQuality = quality;
  }

  setResolution(resolution: string): void {
    this.selectedResolution = resolution;
  }

  /**
   * Start an export - called before rendering begins
   */
  startExport(type: ExportType, filename: string, totalFrames: number = 0): AbortSignal {
    this.abortController = new AbortController();
    
    runInAction(() => {
      this.state = {
        status: 'preparing',
        type,
        progress: 0,
        currentFrame: 0,
        totalFrames,
        remainingMs: 0,
        filename,
        error: null,
        startedAt: Date.now(),
        completedAt: null,
      };
    });

    return this.abortController.signal;
  }

  /**
   * Update progress during export
   */
  updateProgress(current: number, total: number, remainingMs: number): void {
    runInAction(() => {
      this.state.status = 'exporting';
      this.state.currentFrame = current;
      this.state.totalFrames = total;
      this.state.progress = total > 0 ? (current / total) * 100 : 0;
      this.state.remainingMs = remainingMs;
    });
  }

  /**
   * Mark export as completed
   */
  completeExport(): void {
    runInAction(() => {
      this.state.status = 'completed';
      this.state.progress = 100;
      this.state.completedAt = Date.now();
      this.state.remainingMs = 0;
    });
    this.abortController = null;
  }

  /**
   * Mark export as failed
   */
  failExport(error: string): void {
    runInAction(() => {
      this.state.status = 'error';
      this.state.error = error;
      this.state.completedAt = Date.now();
    });
    this.abortController = null;
  }

  /**
   * Cancel an in-progress export
   */
  cancelExport(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    runInAction(() => {
      this.state.status = 'cancelled';
      this.state.completedAt = Date.now();
    });
  }

  /**
   * Reset store to idle state
   */
  reset(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    runInAction(() => {
      this.state = {
        status: 'idle',
        type: 'video',
        progress: 0,
        currentFrame: 0,
        totalFrames: 0,
        remainingMs: 0,
        filename: '',
        error: null,
        startedAt: null,
        completedAt: null,
      };
    });
  }

  /**
   * Dismiss the progress card after completion/error
   */
  dismiss(): void {
    this.reset();
  }
}

export const ExportStore = new ExportStoreClass();
